import { SignalIcon } from "@heroicons/react/20/solid";
import { ButtonPlugins } from "../../components/button";
import HistoryView from "./components/HistoryView";
import TrackingBanners from "./components/TrackingBanners";
import { useParams, useLocation } from "react-router-dom";
import { useOpenTracking } from "./hooks";
import { normalizeCourierServices, statusToTitle } from "./helpers";
import ReactLoading from "react-loading";

export default function Tracking() {
  const { trackingId } = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const environment = queryParams.get("environment");
  const { tracking, loading, error } = useOpenTracking({
    trackingId,
    environment,
  });
  const { companyName } = normalizeCourierServices(tracking.courierCompany);

  if (!loading && tracking.configUseCourierTrackingLink) {
    const redirectedLink =
      tracking.trackingLinkFromCourier || tracking.configFallbackTrackingLink;
    if (redirectedLink) return window.location.replace(redirectedLink);
  }

  return (
    <div>
      {environment === "development" && (
        <div className="w-full text-center text-gray-900 font-bold py-3 bg-warning-400">
          Anda berada dalam tracking page versi mode testing
        </div>
      )}

      {!!loading && (
        <div className="justify-center flex pt-20 h-screen bg-white">
          <div className="flex flex-col items-center">
            <ReactLoading type="spin" color="#4c1d95" height={32} width={32} />
            <p className="mt-1 font-bold">Loading...</p>
          </div>
        </div>
      )}

      {!!error && (
        <div className="h-screen bg-white flex justify-center items-center">
          <div>
            <p className="mb-0 not-italic font-bold text-3xl text-gray-900 text-center">
              Nomor Pesanan Tidak Ditemukan
            </p>
            <p className="mt-2 text-gray-700 not-italic font-normal text-lg text-center">
              Cek kembali nomor pesanan anda
            </p>
          </div>
        </div>
      )}

      {!error && !loading && (
        <div className="md:p-8 p-4 w-full bg-white min-h-screen text-gray-900">
          {/* Above the two-column layout so a notice is the first thing read, on mobile too. */}
          <TrackingBanners banners={tracking.trackingBanners} />

          <div className="md:flex md:space-x-4 space-y-4 md:space-y-0 mt-4">
            <div className="w-full space-y-4">
              {!tracking.configHideDefaultLogo && (
                <div className="border rounded-lg p-6">
                  <img src="/images/biteship-logo.png" className="h-8 mb-4" />
                  <div className="font-bold text-lg">
                    Tracking Page Information
                  </div>
                  <div style={{ color: tracking.configBodyTextColor }}>
                    Powered by Biteship Tracking Page
                  </div>
                </div>
              )}
              {!!tracking.configHeaderLogo && (
                <div className="border rounded-lg p-6 flex items-center">
                  <div className="p-1 w-24 h-24 mr-6 flex justify-center rounded-lg border flex-shrink-0">
                    <img
                      width={64}
                      src={tracking.configHeaderLogo}
                      className="object-contain"
                    />
                  </div>
                  <div className="font-bold text-lg">
                    {tracking.organizationName}
                  </div>
                </div>
              )}
            </div>

            <div className="w-full space-y-4">
              {!!tracking.trackingLinkFromCourier && (
                <div className="border rounded-lg px-6 pt-8 pb-10">
                  <div className="flex items-center mb-4">
                    <SignalIcon
                      className="w-6 mr-2 flex-shrink-0"
                      aria-hidden
                    />
                    <div className="font-bold text-lg">Live Tracking</div>
                  </div>
                  <ButtonPlugins
                    custom
                    onClick={() =>
                      window.location.replace(tracking.trackingLinkFromCourier)
                    }
                    title="Cek Live Tracking"
                    className="h-8"
                    style={{
                      color: "white",
                      backgroundColor: tracking.configBodyTextColor,
                    }}
                  />
                </div>
              )}
              <div className="border rounded-lg p-6 space-y-6">
                <div className="flex space-x-4">
                  <div className="text-lg w-full">
                    <div className="font-bold">Nomor Resi</div>
                    <div className="text-gray-800">{tracking.waybillId}</div>
                  </div>
                  <div className="text-lg w-full">
                    <div className="font-bold">Kurir</div>
                    <div className="text-gray-800">{companyName}</div>
                  </div>
                </div>
                <div className="text-lg">
                  <div className="font-bold">Status</div>
                  <div className="text-gray-800">
                    {statusToTitle(tracking.status)}
                  </div>
                </div>
                <div className="font-bold">
                  <div className="text-lg">Tiba Dalam</div>
                  <div
                    className="text-2xl"
                    style={{ color: tracking.configBodyTextColor }}
                  >
                    {tracking.duration
                      ?.toLowerCase()
                      .replace("days", "Hari")
                      .replace("hours", "Jam")
                      .replace("minutes", "Menit")}{" "}
                    Pengiriman
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full">
              <div className="border rounded-lg">
                <div className="font-bold text-lg p-6">Progress Tracking</div>
                <div className="pl-4 mb-6">
                  <HistoryView
                    history={tracking.history}
                    textColor={tracking.configBodyTextColor}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
